import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationCenterListQuery } from '@invento/shared';
import { notificationsService } from '../api/notifications.service';

const DEFAULT_NOTIFICATION_LIMIT = 10;

export const notificationCenterQueryKeys = {
  all: ['notification-center'] as const,
  listRoot: () => [...notificationCenterQueryKeys.all, 'list'] as const,
  list: (params: Pick<NotificationCenterListQuery, 'limit' | 'includeArchived'>) =>
    [...notificationCenterQueryKeys.listRoot(), params] as const,
  unreadCount: () => [...notificationCenterQueryKeys.all, 'unread-count'] as const,
};

export function useNotificationUnreadCount(enabled = true) {
  return useQuery({
    queryKey: notificationCenterQueryKeys.unreadCount(),
    queryFn: notificationsService.getUnreadCount,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useNotificationCenterList(
  input: { limit?: number; includeArchived?: boolean; enabled?: boolean } = {},
) {
  const limit = input.limit ?? DEFAULT_NOTIFICATION_LIMIT;
  const includeArchived = input.includeArchived ?? false;

  return useInfiniteQuery({
    queryKey: notificationCenterQueryKeys.list({ limit, includeArchived }),
    queryFn: ({ pageParam }) =>
      notificationsService.list({
        page: typeof pageParam === 'number' ? pageParam : 1,
        limit,
        includeArchived,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.meta?.pagination;
      return pagination?.hasNextPage ? pagination.currentPage + 1 : undefined;
    },
    enabled: input.enabled ?? true,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => notificationsService.markRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationCenterQueryKeys.listRoot() });
      queryClient.invalidateQueries({ queryKey: notificationCenterQueryKeys.unreadCount() });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsService.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationCenterQueryKeys.listRoot() });
      queryClient.invalidateQueries({ queryKey: notificationCenterQueryKeys.unreadCount() });
    },
  });
}

export function useArchiveNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => notificationsService.archive(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationCenterQueryKeys.listRoot() });
      queryClient.invalidateQueries({ queryKey: notificationCenterQueryKeys.unreadCount() });
    },
  });
}
